import React, { useEffect, useMemo, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import Select from "react-select";
import Modal from "../Model";
import {
  GET_CATALOG, PREVIEW_COLLECTION, SAVE_COLLECTION, DELETE_COLLECTION,
} from "./graphql";
import {
  SORTS, TABS, inr, parseJson, ruleSummary, formatDate,
} from "./labels";

const input = "border rounded-md px-3 py-2 text-sm w-full";
const num = (v) => (v === "" || v == null ? undefined : Number(v));

const CollectionForm = ({ collection, onClose, onSaved }) => {
  const rule0 = parseJson(collection?.rule);
  const [name, setName] = useState(collection?.name || "");
  const [source, setSource] = useState(collection?.source || "RULE");
  const [gender, setGender] = useState(rule0.gender || "Female");
  const [rule, setRule] = useState({
    categoryIds: rule0.categoryIds || [],
    excludeProductIds: rule0.excludeProductIds || [],
    isPackage: rule0.isPackage === undefined ? "" : String(rule0.isPackage),
    minPrice: rule0.minPrice ?? "",
    maxPrice: rule0.maxPrice ?? "",
    maxDuration: rule0.maxDuration ?? "",
    hasCampaign: Boolean(rule0.hasCampaign),
    createdWithinDays: rule0.createdWithinDays ?? "",
  });
  const [sort, setSort] = useState(collection?.sort || "MOST_BOOKED");
  const [items, setItems] = useState((collection?.items || []).map((i) => ({ serviceId: i.serviceId, badge: i.badge || "", name: i.serviceName, price: i.price })));
  const [pinned, setPinned] = useState(collection?.pinnedServiceIds || []);
  const [isActive, setIsActive] = useState(collection ? collection.isActive : true);
  const [error, setError] = useState("");

  const catalog = useQuery(GET_CATALOG, { variables: { gender } });
  const [runPreview, preview] = useLazyQuery(PREVIEW_COLLECTION, { fetchPolicy: "network-only" });
  const [save, { loading }] = useMutation(SAVE_COLLECTION);
  const cat = catalog.data?.getHomeBuilderCatalog;

  const serviceOptions = useMemo(
    () => (cat?.services || []).map((s) => ({ value: s.id, label: `${s.name} · ${inr(s.price)}${s.categoryName ? ` · ${s.categoryName}` : ""}`, s })),
    [cat],
  );
  const categoryOptions = useMemo(() => (cat?.categories || []).map((c) => ({ value: c.id, label: c.name })), [cat]);
  const productOptions = useMemo(() => (cat?.products || []).map((p) => ({ value: p.id, label: p.name })), [cat]);

  const ruleJson = () => {
    const r = {
      gender,
      categoryIds: rule.categoryIds.length ? rule.categoryIds : undefined,
      excludeProductIds: rule.excludeProductIds.length ? rule.excludeProductIds : undefined,
      isPackage: rule.isPackage === "" ? undefined : rule.isPackage === "true",
      minPrice: num(rule.minPrice),
      maxPrice: num(rule.maxPrice),
      maxDuration: num(rule.maxDuration),
      hasCampaign: rule.hasCampaign || undefined,
      createdWithinDays: num(rule.createdWithinDays),
    };
    return JSON.stringify(r);
  };
  const payload = () => ({
    id: collection?.id,
    name: name.trim(),
    source,
    rule: source === "RULE" ? ruleJson() : JSON.stringify({ gender }),
    sort: source === "MANUAL" ? "MANUAL" : sort,
    pinnedServiceIds: source === "RULE" ? pinned : [],
    isActive,
    items: source === "MANUAL" ? items.map((i) => ({ serviceId: i.serviceId, badge: i.badge || null })) : [],
  });

  // Live preview as the rule changes.
  useEffect(() => {
    const t = setTimeout(() => {
      if (source === "MANUAL" && !items.length) return;
      runPreview({ variables: { data: payload(), gender } });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, gender, rule, sort, items, pinned]);

  const setR = (k) => (e) => setRule({ ...rule, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const moveItem = (i, d) => {
    const next = [...items];
    const j = i + d;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) { setError("Give the collection a name."); return; }
    if (source === "MANUAL" && !items.length) { setError("Pick at least one service."); return; }
    const res = await save({ variables: { data: payload() } });
    const out = res.data?.saveCollection;
    if (out?.status !== "SUCCESS") { setError(out?.message || "Could not save"); return; }
    onSaved();
  };

  const p = preview.data?.previewCollection;

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <div className="flex items-start justify-between">
        <h2 className="text-lg font-semibold">{collection ? "Edit collection" : "New collection"}</h2>
        <button type="button" onClick={onClose} className="text-gray-500 hover:text-black text-xl leading-none" aria-label="Close">×</button>
      </div>
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">Name</span>
        <input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Packages under ₹800" />
        <span className="text-xs text-gray-500">Admins see this name; customers see the section title you set on the home layout.</span>
      </label>
      <div className="flex flex-wrap gap-6 text-sm">
        <div className="flex flex-col gap-1">
          <span className="font-medium">Tab</span>
          <div className="flex gap-2">
            {TABS.map((t) => (
              <button type="button" key={t.gender} onClick={() => setGender(t.gender)} className={`px-3 py-1.5 rounded-md ${gender === t.gender ? "bg-blue-600 text-white" : "bg-gray-100"}`}>{t.label}</button>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="font-medium">How services are chosen</span>
          <div className="flex gap-2">
            <button type="button" onClick={() => setSource("RULE")} className={`px-3 py-1.5 rounded-md ${source === "RULE" ? "bg-blue-600 text-white" : "bg-gray-100"}`}>Automatically, by rule</button>
            <button type="button" onClick={() => setSource("MANUAL")} className={`px-3 py-1.5 rounded-md ${source === "MANUAL" ? "bg-blue-600 text-white" : "bg-gray-100"}`}>I pick them</button>
          </div>
        </div>
      </div>

      {source === "RULE" && (
        <div className="flex flex-col gap-3 rounded-lg bg-gray-50 p-3">
          <p className="text-xs text-gray-600">Every active service matching all of these is included. New services that match join automatically.</p>
          <label className="flex flex-col gap-1 text-sm"><span>Categories (any of)</span>
            <Select isMulti options={categoryOptions} value={categoryOptions.filter((o) => rule.categoryIds.includes(o.value))} onChange={(v) => setRule({ ...rule, categoryIds: v.map((o) => o.value) })} placeholder="All categories" />
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <label className="flex flex-col gap-1"><span>Type</span>
              <select className={input} value={rule.isPackage} onChange={setR("isPackage")}>
                <option value="">Packages and services</option>
                <option value="true">Packages only</option>
                <option value="false">Single services only</option>
              </select>
            </label>
            <label className="flex flex-col gap-1"><span>Price from (₹)</span><input type="number" min={0} className={input} value={rule.minPrice} onChange={setR("minPrice")} /></label>
            <label className="flex flex-col gap-1"><span>Price up to (₹)</span><input type="number" min={0} className={input} value={rule.maxPrice} onChange={setR("maxPrice")} /></label>
            <label className="flex flex-col gap-1"><span>Duration up to (min)</span><input type="number" min={0} className={input} value={rule.maxDuration} onChange={setR("maxDuration")} /></label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <label className="flex flex-col gap-1"><span>Leave out these product lines</span>
              <Select isMulti options={productOptions} value={productOptions.filter((o) => rule.excludeProductIds.includes(o.value))} onChange={(v) => setRule({ ...rule, excludeProductIds: v.map((o) => o.value) })} placeholder="None" />
            </label>
            <label className="flex flex-col gap-1"><span>Added in the last (days)</span><input type="number" min={1} className={input} value={rule.createdWithinDays} onChange={setR("createdWithinDays")} placeholder="Any time" /></label>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={rule.hasCampaign} onChange={setR("hasCampaign")} /> Only services with a live campaign price</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <label className="flex flex-col gap-1"><span>Order</span>
              <select className={input} value={sort} onChange={(e) => setSort(e.target.value)}>
                {Object.entries(SORTS).filter(([k]) => k !== "MANUAL").map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1"><span>Always show first (pin)</span>
              <Select isMulti options={serviceOptions} value={pinned.map((id) => serviceOptions.find((o) => o.value === id)).filter(Boolean)} onChange={(v) => setPinned(v.map((o) => o.value))} placeholder="Nothing pinned" />
            </label>
          </div>
        </div>
      )}

      {source === "MANUAL" && (
        <div className="flex flex-col gap-3 rounded-lg bg-gray-50 p-3">
          <Select
            options={serviceOptions.filter((o) => !items.some((i) => i.serviceId === o.value))}
            value={null}
            onChange={(o) => o && setItems([...items, { serviceId: o.value, badge: "", name: o.s.name, price: o.s.price }])}
            placeholder="Search a service to add…"
          />
          <ol className="flex flex-col gap-1">
            {items.map((it, i) => (
              <li key={it.serviceId} className="flex items-center gap-2 bg-white rounded-md border px-2 py-1.5 text-sm">
                <span className="w-5 text-gray-400 text-xs">{i + 1}</span>
                <span className="flex-1 truncate">{it.name} <span className="text-gray-500">{inr(it.price)}</span></span>
                <input className="border rounded px-2 py-1 text-xs w-28" placeholder="Badge (optional)" value={it.badge} onChange={(e) => setItems(items.map((x, k) => (k === i ? { ...x, badge: e.target.value } : x)))} maxLength={16} />
                <button type="button" onClick={() => moveItem(i, -1)} className="px-1 text-xs" aria-label="Move up">▲</button>
                <button type="button" onClick={() => moveItem(i, 1)} className="px-1 text-xs" aria-label="Move down">▼</button>
                <button type="button" onClick={() => setItems(items.filter((_, k) => k !== i))} className="px-1 text-red-600" aria-label="Remove">×</button>
              </li>
            ))}
            {!items.length && <li className="text-sm text-gray-500">No services picked yet.</li>}
          </ol>
        </div>
      )}

      <div className="rounded-lg border p-3">
        <div className="text-sm font-medium mb-2">
          {preview.loading ? "Checking…" : p ? `${p.totalCount} service${p.totalCount === 1 ? "" : "s"} match` : "Preview"}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {(p?.services || []).slice(0, 12).map((s) => (
            <div key={s.id} className="flex-none w-28 text-xs">
              <div className="aspect-square rounded-md bg-gray-100 overflow-hidden">{s.image && <img src={s.image.startsWith("http") ? s.image : `https://api.barbera.in${s.image}`} alt="" className="w-full h-full object-cover" />}</div>
              <div className="font-medium leading-tight mt-1 line-clamp-2">{s.name}</div>
              <div className="text-gray-600">{inr(s.price)}{s.variant ? ` · ${s.variant}` : ""}</div>
            </div>
          ))}
          {p && p.totalCount === 0 && <p className="text-sm text-amber-700">Nothing matches. Loosen the rule.</p>}
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> Collection is on (when off, its rails disappear from the app)</label>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-4 py-2 rounded-md bg-gray-100">Cancel</button>
        <button type="submit" disabled={loading} className="px-5 py-2 rounded-md bg-[#FBB033] font-semibold disabled:opacity-60">{loading ? "Saving…" : "Save collection"}</button>
      </div>
    </form>
  );
};

const CollectionsPanel = ({ collections, loading, canEdit, onChanged }) => {
  const [editing, setEditing] = useState(null);
  const [deleteCollection] = useMutation(DELETE_COLLECTION);
  const femaleCats = useQuery(GET_CATALOG, { variables: { gender: "Female" } });
  const maleCats = useQuery(GET_CATALOG, { variables: { gender: "Male" } });
  const categoryNames = useMemo(() => {
    const m = {};
    [femaleCats, maleCats].forEach((q) => (q.data?.getHomeBuilderCatalog?.categories || []).forEach((c) => { m[c.id] = c.name; }));
    return m;
  }, [femaleCats, maleCats]);

  const remove = async (c) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the collection "${c.name}"?`)) return;
    const res = await deleteCollection({ variables: { id: c.id } });
    const out = res.data?.deleteCollection;
    // eslint-disable-next-line no-alert
    if (out?.status !== "SUCCESS") window.alert(out?.message || "Could not delete");
    onChanged();
  };

  return (
    <div className="bg-white rounded-lg border">
      <div className="flex flex-wrap items-center justify-between gap-3 p-3 border-b">
        <p className="text-sm text-gray-600">Reusable lists of services. A home rail shows the first few; See all shows the rest.</p>
        {canEdit && <button onClick={() => setEditing({})} className="px-4 py-2 bg-[#FBB033] rounded-md text-sm font-semibold">+ New collection</button>}
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-600">
            <tr>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Services</th>
              <th className="px-3 py-2">Order</th>
              <th className="px-3 py-2">Used in</th>
              <th className="px-3 py-2">Updated</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-500">Loading…</td></tr>}
            {collections.map((c) => (
              <tr key={c.id} className={`border-t ${c.isActive ? "" : "opacity-50"}`}>
                <td className="px-3 py-2 font-medium">{c.name}{!c.isActive && <span className="ml-2 text-xs text-gray-500">off</span>}</td>
                <td className="px-3 py-2 text-gray-600">
                  {c.source === "MANUAL" ? `Hand-picked: ${c.items.length} service${c.items.length === 1 ? "" : "s"}` : ruleSummary(c.rule, categoryNames)}
                </td>
                <td className="px-3 py-2 text-gray-600">{SORTS[c.sort]}</td>
                <td className="px-3 py-2 text-gray-600">{c.usedInSections ? `${c.usedInSections} section${c.usedInSections > 1 ? "s" : ""}` : "Not used"}</td>
                <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{formatDate(c.updatedAt)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {canEdit && <button onClick={() => setEditing(c)} className="px-2 py-1 rounded hover:bg-gray-100">Edit</button>}
                  {canEdit && <button onClick={() => remove(c)} className="px-2 py-1 rounded hover:bg-red-50 text-red-600">Delete</button>}
                </td>
              </tr>
            ))}
            {!loading && !collections.length && <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-500">No collections yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && (
        <Modal onClose={() => setEditing(null)} disableOutsideClick>
          <CollectionForm collection={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />
        </Modal>
      )}
    </div>
  );
};

export default CollectionsPanel;
